import { ReferentRows } from '@/components/admin/versioned/referent-rows';
import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import type { VersionedWording } from '@/lib/versioned-labels';
import { VERSION_TAG_LABEL, versionedDay } from '@/lib/versioned-labels';
import type { UsageLine, VersionSummary, VersionTag } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

/**
 * The confirmation of a pointer move: « Passer en production », « Reculer
 * développement » (refonte modulaire §4; maquette `PasserEnProduction`).
 *
 * ## It SHOWS THE EFFECT, and §4 asks for exactly three things
 *
 * « La version visée, les référents qui la joueront (ceux de "utilisé dans"
 * pour le tag concerné, ou "utilisé nulle part encore"), et la phrase "les
 * sessions en cours ne changent pas". » All three are here, and the third is
 * the one that could not be left out: moving `production` is the gesture that
 * looks like it might rewrite history, and the freeze at session start is the
 * whole reason it does not (§4). An operator who has to go and check that in a
 * spec has already hesitated too long.
 *
 * The box also names what is in production TODAY, with its date, its author and
 * its note. A confirmation that names only the destination asks somebody to
 * remember where they are.
 *
 * ## Nothing is copied
 *
 * The sentence says so, because a rollback that copied a version would break
 * the linearity the history depends on: « le rollback déplace un pointeur, il
 * ne copie pas une version » (§4).
 */

export interface PointerDialogProps {
  wording: VersionedWording;
  name: string;
  /** Which pointer is moving. */
  tag: VersionTag;
  /** Where it is going. */
  target: VersionSummary;
  /** Where it is now, or null when `production` has never been posed. */
  current: VersionSummary | null;
  usage: readonly UsageLine[];
  /**
   * Who plays each pointer of the entity, when the object knows it better than
   * `usage` (review of !462, issue #148): the list of the moving pointer is
   * drawn as given, each line with its own pointers that play it. Absent,
   * `usage` filtered by tag - the layer's rule.
   */
  referentsByTag?: Readonly<Record<VersionTag, readonly UsageLine[]>>;
  busy: boolean;
  error: string | null;
  /**
   * What the object refuses about the version this pointer is moving ONTO, and
   * whether that refusal blocks the gesture (§7.1).
   *
   * Null for an object that refuses nothing of its own - the criterion and the
   * persona pass it never - and null too while the answer is not known yet: a
   * box that blocked during a read would refuse a legitimate move for as long
   * as the read took. The server refuses either way; this is what spares an
   * operator the round trip, the way the save box already does.
   */
  blockers?: { content: ReactNode; blocked: boolean } | null;
  /**
   * No prod is linked to this server (`PROD_ORIGIN` unset): the passage to production stays
   * local, as it was before the vitrine, and the box says so (vitrine.md §4.1).
   */
  noProd?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PointerDialog({
  wording,
  name,
  tag,
  target,
  current,
  usage,
  referentsByTag,
  busy,
  error,
  blockers = null,
  noProd = false,
  onConfirm,
  onCancel,
}: PointerDialogProps) {
  const label = VERSION_TAG_LABEL[tag];
  // The same gesture in the two directions (vitrine.md §4.1): a version ahead of « latest prod »
  // is a passage, one behind it a move back.
  const movesBack = tag === 'production' && current !== null && target.number < current.number;
  const title =
    tag === 'production'
      ? movesBack
        ? `Reculer ${label} sur la v${target.number}`
        : `Passer la v${target.number} en production`
      : `Reculer ${label} sur la v${target.number}`;

  return (
    <VersionedDialog
      data-testid={`${wording.entity}-boite-pointeur`}
      title={title}
      description={`${wording.subject(name)}. Le pointeur ${label} ${current === null ? `se pose sur la v${target.number}` : `quitte la v${current.number} pour la v${target.number}`} ; rien n’est copié.`}
      confirmLabel={
        tag === 'production' && !movesBack ? 'Passer en production' : `Reculer ${label}`
      }
      busy={busy}
      error={error}
      confirmDisabled={blockers?.blocked ?? false}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <VersionedDialogSection title={`Aujourd’hui en ${label}`}>
        {current === null ? (
          <p className="text-gray-500 text-small italic">
            Rien n’a jamais été passé en production.
          </p>
        ) : (
          <p className="text-gray-800 text-small">
            v{current.number}, depuis le {versionedDay(current.createdAt)}{' '}
            <span className="text-gray-600">
              · {current.createdBy}
              {current.note === null || current.note === '' ? '' : ` · « ${current.note} »`}
            </span>
          </p>
        )}
      </VersionedDialogSection>

      <VersionedDialogSection title={`À partir de maintenant, la v${target.number} sera jouée par`}>
        {/* « Utilisé nulle part encore » SURVIVES here, where the card and the
            editor panel dropped it (§4): this box answers « qui jouera cette
            version », and an empty list is an answer to that - not the zero
            count a fresh card would have carried. */}
        <ReferentRows
          entity={wording.entity}
          usage={referentsByTag?.[tag] ?? usage}
          tag={tag}
          emptyMessage="Utilisé nulle part encore."
          {...(referentsByTag === undefined ? {} : { byReference: true })}
        />
      </VersionedDialogSection>

      {/* Inert while the move is in flight, like the save box's (second review
          of !474): its doors out of the box must not close it over a request. */}
      {blockers === null ? null : (
        <div inert={busy} className={busy ? 'opacity-60' : undefined}>
          {blockers.content}
        </div>
      )}

      {/* Where no prod is linked (a workstation, the CI), « latest prod » is a pointer of this
          server alone: the box says so rather than let it pass for a passage to the prod. */}
      {tag === 'production' && noProd ? (
        <Alert tone="info" title="Aucune prod reliée">
          <span data-testid={`${wording.entity}-passage-local`}>
            Aucune prod reliée : le passage reste local.
          </span>
        </Alert>
      ) : null}

      <Alert tone="info" title="Les sessions en cours ne changent pas">
        Chacune garde les versions gelées à son démarrage. Seules les sessions qui démarrent après
        ce geste jouent la v{target.number}.
      </Alert>
    </VersionedDialog>
  );
}
